/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { addDiscountCodeAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { Cart, Coupon } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { ICartActionResultWithCart } from './cart-action-result';

export default async function addPromoCode(
    cart: Readonly<Cart | undefined>,
    promoCode: string,
    actionContext: IActionContext
): Promise<ICartActionResultWithCart> {
    if (!cart) {
        return { cart: undefined, status: 'FAILED', substatus: 'EMPTYCART' };
    }

    if (hasPromoCode(cart, promoCode)) {
        return { cart: undefined, status: 'FAILED', substatus: 'ALREADYADDED' };
    }

    return addDiscountCodeAsync({ callerContext: actionContext }, cart.Id, promoCode)
        .then(newCart => {
            // Retail server will still return success if a promo code is not valid,
            // but it won't actually add that promo code. So need to check if promo
            // code is actually in the new cart
            if (hasPromoCode(newCart, promoCode)) {
                return <ICartActionResultWithCart>{ cart: newCart, status: 'SUCCESS' };
            }
            return <ICartActionResultWithCart>{ cart: undefined, status: 'FAILED', substatus: 'INVALIDPROMOCODE' };
        })
        .catch(error => {
            actionContext.telemetry.warning(error);
            const resultWithCart: ICartActionResultWithCart = {
                cart: undefined,
                status: 'FAILED',
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- existing error type.
                errorDetails: { LocalizedMessage: error.message }
            };

            return resultWithCart;
        });
}

function hasPromoCode(cart: Cart, code: string): boolean {
    const codes = cart.Coupons
        ? cart.Coupons.map((coupon: Coupon) => {
              return coupon.Code!.toLowerCase();
          })
        : [];
    return codes.includes(code.toLowerCase());
}
